import type { MusteriTeslimat } from "../../../mocks/musteriPaneli";

interface Props {
  teslimat: MusteriTeslimat;
}

const durumStyle: Record<
  MusteriTeslimat["durum"],
  { badge: string; dot: string; label: string; border: string; bg: string }
> = {
  Hazır: {
    badge: "bg-sky-100 text-sky-700",
    dot: "bg-sky-500",
    label: "Teslimata Hazır",
    border: "border-sky-200",
    bg: "bg-sky-50/60",
  },
  "Taşıma Halinde": {
    badge: "bg-amber-100 text-amber-800",
    dot: "bg-amber-500",
    label: "Yolda!",
    border: "border-amber-200",
    bg: "bg-amber-50/80",
  },
  "Teslim Edildi": {
    badge: "bg-green-100 text-green-700",
    dot: "bg-green-500",
    label: "Teslim Edildi",
    border: "border-green-200",
    bg: "bg-green-50/60",
  },
};

const STEPS = ["Hazır", "Taşıma Halinde", "Teslim Edildi"];

export default function DeliveryTracker({ teslimat }: Props) {
  const d = durumStyle[teslimat.durum];
  const stepIdx = STEPS.indexOf(teslimat.durum);
  const isLive = teslimat.durum === "Taşıma Halinde";
  const isDone = teslimat.durum === "Teslim Edildi";

  return (
    <div className={`rounded-farm border ${d.border} overflow-hidden`}>
      <div className={`px-4 py-3 flex items-center justify-between ${d.bg} border-b ${d.border}`}>
        <div className="flex items-center gap-2">
          <i
            className={`ri-truck-line text-base ${
              stepIdx >= 1 ? "text-amber-600" : "text-sky-600"
            }`}
          />
          <span className="font-heading font-semibold text-sm text-farm-brown">
            Teslimat Takibi
          </span>
        </div>

        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${d.badge}`}
        >
          {isLive ? (
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping inline-block" />
          ) : (
            <span className={`w-1.5 h-1.5 rounded-full ${d.dot} inline-block`} />
          )}
          {d.label}
        </span>
      </div>

      <div className="px-4 py-3 space-y-3 bg-farm-warm">
        <div className="flex items-center gap-0">
          {STEPS.map((s, i) => {
            const done = i < stepIdx;
            const active = i === stepIdx;
            const stepIcons = ["ri-box-3-line", "ri-truck-line", "ri-checkbox-circle-line"];

            return (
              <div key={s} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center border-2 ${
                      done
                        ? "bg-farm-green border-farm-green text-white"
                        : active
                        ? `${
                            isLive
                              ? "bg-amber-500 border-amber-500"
                              : "bg-sky-500 border-sky-500"
                          } text-white`
                        : "bg-farm-cream border-farm-border text-farm-muted"
                    }`}
                  >
                    <i className={`${stepIcons[i]} text-xs`} />
                  </div>

                  <span
                    className={`text-[9px] mt-0.5 whitespace-nowrap font-medium ${
                      active
                        ? "font-bold text-farm-brown"
                        : done
                        ? "text-farm-green"
                        : "text-farm-muted"
                    }`}
                  >
                    {s}
                  </span>
                </div>

                {i < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-0.5 mb-3.5 ${
                      i < stepIdx ? "bg-farm-green" : "bg-farm-border"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-start gap-3 bg-farm-cream rounded-farm px-3 py-2.5">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-farm-earth-pale text-farm-earth flex-shrink-0">
            <i className="ri-steering-2-line text-base" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-farm-brown">{teslimat.tasiyiciAdi}</p>
            <p className="text-[10px] text-farm-muted">
              {teslimat.arac || "Taşıyıcı atandı"}
            </p>
          </div>

          {teslimat.tasiyiciTelefon && (
            <a
              href={`tel:${teslimat.tasiyiciTelefon}`}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-farm bg-farm-green-pale text-farm-green text-xs font-semibold border border-farm-green/20 hover:bg-farm-green hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-xs" />
              Ara
            </a>
          )}
        </div>

        <div className="flex items-start gap-2">
          <div className="w-5 h-5 flex items-center justify-center text-farm-muted flex-shrink-0 mt-0.5">
            <i className="ri-map-pin-line text-sm" />
          </div>
          <p className="text-xs text-farm-brown leading-relaxed">{teslimat.adres}</p>
        </div>

        {!isDone && teslimat.canliKonumLinki && (
          <a
            href={teslimat.canliKonumLinki}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-farm font-semibold text-sm border transition-colors cursor-pointer ${
              isLive
                ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-500"
                : "bg-farm-earth-pale border-farm-earth/20 text-farm-earth hover:bg-farm-earth hover:text-white"
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${isLive ? "ri-live-line" : "ri-map-pin-line"} text-base`} />
            </div>
            {isLive ? "Canlı Konumu Takip Et" : "Haritada Gör"}
          </a>
        )}

        {!isDone && !teslimat.canliKonumLinki && (
          <div className="flex items-center justify-center gap-2 w-full py-2 rounded-farm border border-dashed border-farm-border text-[11px] text-farm-muted bg-farm-cream/70">
            <i className="ri-map-pin-user-line text-sm" />
            Canlı konum henüz paylaşılmadı
          </div>
        )}

        {teslimat.not && (
          <div className="rounded-farm bg-farm-cream border border-farm-border px-3 py-2 text-[11px] text-farm-brown leading-relaxed">
            <span className="font-semibold">Not:</span> {teslimat.not}
          </div>
        )}

        {isDone && (
          <div className="flex items-center justify-center gap-2 py-2.5 rounded-farm bg-farm-green-pale border border-farm-green/20">
            <i className="ri-check-double-line text-farm-green text-lg" />
            <span className="text-sm font-bold text-farm-green">
              Teslim Edildi
              {teslimat.teslimSaati && (
                <span className="font-normal text-farm-muted ml-1">
                  · {teslimat.teslimSaati}
                </span>
              )}
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap text-[10px] text-farm-muted border-t border-farm-border pt-2">
          <span>
            Hayvan:{" "}
            <span className="font-mono font-semibold text-farm-brown">
              {teslimat.hayvanKupeNo}
            </span>
          </span>
          <span>·</span>
          <span>
            Hisse:{" "}
            <span className="font-semibold text-farm-brown">{teslimat.hisseNo}</span>
          </span>
        </div>
      </div>
    </div>
  );
}